import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { connectorsApi } from '@/api/connectors'
import { useTenantKey } from './useTenantKey'
import { invalidateClaudeAuthAvailability, invalidateCopilotAuthAvailability } from './useProjectSettings'
import type { ConnectorType, SaveTokenRequest, SaveDatabaseConnectorRequest } from '@/types'

export function useConnectors(projectId: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'connectors'),
    queryFn: () => connectorsApi.getAll(projectId),
    enabled: !!projectId,
  })
}

export function useConnectionStatus(projectId: string, connectorId: string, enabled: boolean) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'connectors', 'connection-status', connectorId),
    queryFn: () => connectorsApi.getConnectionStatus(projectId, connectorId),
    enabled: enabled && !!projectId && !!connectorId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

const AZURE_DEVOPS_LOOKUP_KEYS: readonly (readonly string[])[] = [
  ['azure-devops'],
  ['work-items', 'azure-devops-process-config'],
  ['work-items', 'azure-devops-projects'],
  ['work-items', 'azure-devops-epics'],
  ['work-items', 'azure-devops-features'],
]

function invalidateAzureDevOpsLookups(
  queryClient: QueryClient,
  tenantKey: ReturnType<typeof useTenantKey>,
  projectId: string,
) {
  AZURE_DEVOPS_LOOKUP_KEYS.forEach(segments => {
    queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, ...segments) })
  })
}

export function useSaveToken(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ connectorType, data }: { connectorType: ConnectorType; data: SaveTokenRequest }) =>
      connectorsApi.saveToken(projectId, connectorType, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'connectors') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-trigger-schema') })
      invalidateAzureDevOpsLookups(queryClient, tenantKey, projectId)
      invalidateClaudeAuthAvailability(queryClient)
      invalidateCopilotAuthAvailability(queryClient)
    },
  })
}

export const TITAN_ENDPOINT_STATUS_KEY = 'qubiz-titan-endpoint-status'

export function useTitanEndpointStatus(projectId: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey(TITAN_ENDPOINT_STATUS_KEY, projectId),
    queryFn: () => connectorsApi.getTitanEndpointStatus(projectId),
    enabled: !!projectId,
    staleTime: 0,
  })
}

export function useSaveDatabaseConnector(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (data: SaveDatabaseConnectorRequest) => connectorsApi.saveDatabaseConnector(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'connectors') })
    },
  })
}

export function useDeleteDatabaseConnector(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (connectorId: string) => connectorsApi.deleteDatabaseConnector(projectId, connectorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'connectors') })
    },
  })
}

export function useDeleteConnectorInstance(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ connectorType, connectorId }: { connectorType: ConnectorType; connectorId: string }) =>
      connectorsApi.deleteConnectorInstance(projectId, connectorType, connectorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'connectors') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'context-sources') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-trigger-schema') })
      invalidateAzureDevOpsLookups(queryClient, tenantKey, projectId)
    },
  })
}

export function useDisconnectConnector(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (connectorType: ConnectorType) => connectorsApi.disconnect(projectId, connectorType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'connectors') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'context-sources') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-trigger-schema') })
      invalidateAzureDevOpsLookups(queryClient, tenantKey, projectId)
      invalidateClaudeAuthAvailability(queryClient)
      invalidateCopilotAuthAvailability(queryClient)
    },
  })
}

export function useProviderRepositories(projectId: string, connectorType: ConnectorType, enabled: boolean, search = '', connectorId?: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'connectors', connectorType, 'repositories', search, connectorId ?? null),
    queryFn: () => connectorsApi.listRepositories(projectId, connectorType, search || undefined, connectorId),
    enabled: enabled && !!projectId,
    staleTime: 60 * 1000,
  })
}

export function useProviderRepoBranches(projectId: string, connectorType: ConnectorType, repository: string, search: string, enabled: boolean, connectorId?: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'connectors', connectorType, 'branches', repository, search, connectorId ?? null),
    queryFn: () => connectorsApi.listBranches(projectId, connectorType, repository, search || undefined, connectorId),
    enabled: enabled && !!projectId && !!repository,
    staleTime: 60 * 1000,
  })
}

export function useProviderRepoFiles(projectId: string, connectorType: ConnectorType, repository: string, branch: string, search: string, enabled: boolean, connectorId?: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'connectors', connectorType, 'files', repository, branch, search, connectorId ?? null),
    queryFn: () => connectorsApi.listFiles(projectId, connectorType, repository, branch || undefined, search || undefined, connectorId),
    enabled: enabled && !!projectId && !!repository,
    staleTime: 60 * 1000,
  })
}

export function useAzureDevOpsWikiProjects(projectId: string, enabled: boolean, connectorId?: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'connectors', 'azure-devops', 'wiki-projects', connectorId ?? null),
    queryFn: () => connectorsApi.listWikiProjects(projectId, connectorId),
    enabled: enabled && !!projectId,
  })
}

export function useAzureDevOpsWikis(projectId: string, project: string, enabled: boolean, connectorId?: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'connectors', 'azure-devops', 'wikis', project, connectorId ?? null),
    queryFn: () => connectorsApi.listWikis(projectId, project, connectorId),
    enabled: enabled && !!projectId && !!project,
  })
}

export function useAzureDevOpsWikiPages(projectId: string, project: string, wikiId: string, enabled: boolean, connectorId?: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'connectors', 'azure-devops', 'wiki-pages', project, wikiId, connectorId ?? null),
    queryFn: () => connectorsApi.listWikiPages(projectId, project, wikiId, connectorId),
    enabled: enabled && !!projectId && !!project && !!wikiId,
  })
}

export function useAzureDevOpsAllWikis(projectId: string, enabled: boolean, connectorId?: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'connectors', 'azure-devops', 'all-wikis', connectorId ?? null),
    queryFn: () => connectorsApi.listAllWikis(projectId, connectorId),
    enabled: enabled && !!projectId,
  })
}
